import type { FilterType, ListSortColumn, SortDir, SortType } from "./document-list.types"

/** Title-case / underscore humanize for unknown document types. */
function humanizeDocumentType(type: string): string {
  return type
    .replace(/[-_]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const FILTER_TYPE_CHIP_LABELS: Record<string, string> = {
  work_spec: "Work Specs",
  voice_memo: "Voice Memos",
  note: "Notes",
  quiz: "Quizzes",
  flow: "Flows",
  meeting: "Meetings",
  html: "HTML",
  csv: "CSV",
  image: "Images",
  incident: "Incidents",
  skill: "Skills",
  soul: "Soul",
  idea: "Ideas",
  todo: "Todos",
  proposal: "Proposals",
  email: "Email",
  personal: "Personal",
  project: "Projects",
  technical: "Technical",
  design: "Design",
  architecture: "Architecture",
  ai_suggestion: "AI Suggestions",
  product_board: "Product Board",
}

const LIST_TYPE_LABELS: Record<string, string> = {
  work_spec: "Work Spec",
  voice_memo: "Voice Memo",
  note: "Note",
  quiz: "Quiz",
  flow: "Flow",
  meeting: "Meeting",
  html: "HTML",
  csv: "CSV",
  image: "Image",
  incident: "Incident",
  skill: "Skill",
  soul: "Soul",
  idea: "Idea",
  todo: "Todo",
  proposal: "Proposal",
  email: "Email",
  personal: "Personal",
  project: "Project",
  technical: "Technical",
  design: "Design",
  architecture: "Architecture",
  ai_suggestion: "AI Suggestion",
  product_board: "Product Board",
}

/**
 * Label for type filter chips (smart chips in folder view).
 * Never returns "All Types" for a concrete type - that label was misleading.
 */
export function getFilterTypeChipLabel(type: FilterType): string {
  if (!type || type === "all") return "All"
  const key = String(type).trim().toLowerCase().replace(/-/g, "_")
  return FILTER_TYPE_CHIP_LABELS[key] ?? humanizeDocumentType(key)
}

/** Short type label for list/grid cells (singular). */
export function getListTypeLabel(type: string | undefined | null): string {
  if (!type) return ""
  const key = String(type).trim().toLowerCase().replace(/-/g, "_")
  return LIST_TYPE_LABELS[key] ?? humanizeDocumentType(key)
}

export function getSortColumnLabel(column: ListSortColumn): string {
  switch (column) {
    case "name":
      return "Name"
    case "type":
      return "Type"
    case "modified":
      return "Modified"
    case "lines":
      return "Lines"
    case "size":
      return "Size"
    case "author":
      return "Author"
  }
}

/** Compact toolbar sort label for card/grid (legacy Most Recent wording). */
export function getSortMenuLabel(column: ListSortColumn, dir: SortDir): string {
  if (column === "modified" && dir === "desc") return "Most Recent"
  if (column === "modified" && dir === "asc") return "Oldest First"
  if (column === "name" && dir === "asc") return "Name (A-Z)"
  if (column === "name" && dir === "desc") return "Name (Z-A)"
  if (column === "type") return dir === "asc" ? "Type (A-Z)" : "Type (Z-A)"
  if (column === "author") return dir === "asc" ? "Author (A-Z)" : "Author (Z-A)"
  if (column === "lines") return dir === "desc" ? "Lines (most)" : "Lines (fewest)"
  if (column === "size") return dir === "desc" ? "Size (largest)" : "Size (smallest)"
  return `${getSortColumnLabel(column)}`
}

/** @deprecated Use getSortMenuLabel(column, dir). Maps old API sort keys. */
export function getSortMenuLabelFromApi(sort: SortType): string {
  switch (sort) {
    case "recent":
      return "Most Recent"
    case "oldest":
      return "Oldest First"
    case "title-asc":
      return "Name (A-Z)"
    case "title-desc":
      return "Name (Z-A)"
  }
}
